import React, { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { API, ITEM_TYPES, formatDate, DEPARTMENT_LABELS, DEPARTMENT_COLORS, cn } from "../lib/utils";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Checkbox } from "../components/ui/checkbox";
import { Switch } from "../components/ui/switch";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Separator } from "../components/ui/separator";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Calendar } from "../components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "../components/ui/popover";
import { toast } from "sonner";
import { CalendarIcon, Plus, X, Package, ArrowLeft, Loader2, ChevronDown, ChevronUp, Settings2 } from "lucide-react";
import { format } from "date-fns";

export default function CreateOrderPage() {
  const navigate = useNavigate();
  const [customers, setCustomers] = useState([]);
  const [items, setItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [taskTemplates, setTaskTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [showTaskTemplates, setShowTaskTemplates] = useState(false);

  const [formData, setFormData] = useState({
    customer_id: "",
    target_delivery_date: null,
    notes: "",
    po_number: "",
    shipping_address: "",
    billing_address: "",
    payment_terms: "",
    items: [],
    task_templates: [],
    submit_for_approval: false,
  });

  const [selectedItemId, setSelectedItemId] = useState("");
  const [itemQuantity, setItemQuantity] = useState(1);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [customersRes, itemsRes, templatesRes, categoriesRes] = await Promise.all([
          axios.get(`${API}/customers`, { withCredentials: true }),
          axios.get(`${API}/items`, { withCredentials: true }),
          axios.get(`${API}/task-templates`, { withCredentials: true }),
          axios.get(`${API}/categories`, { withCredentials: true }),
        ]);
        setCustomers(customersRes.data);
        setItems(itemsRes.data);
        setCategories(categoriesRes.data);
        
        // Initialize task templates with enabled status
        const templates = templatesRes.data.map(t => ({
          ...t,
          is_enabled: true,
        }));
        setTaskTemplates(templates);
        setFormData(prev => ({ ...prev, task_templates: templates }));
      } catch (err) {
        console.error("Failed to fetch data:", err);
        toast.error("Failed to load data");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const handleAddItem = () => {
    if (!selectedItemId) return;

    const item = items.find((i) => i.item_id === selectedItemId);
    if (!item) return;

    if (formData.items.some((i) => i.item_id === selectedItemId)) {
      toast.error("Item already added");
      return;
    }

    // Find category name for display
    const catName = item.category_id
      ? (categories || []).find(c => c.category_id === item.category_id)?.name || ""
      : "";

    setFormData((prev) => ({
      ...prev,
      items: [
        ...prev.items,
        {
          item_id: item.item_id,
          item_name: item.name,
          quantity: itemQuantity || 1,
          category_id: item.category_id || null,
          category_name: catName,
          notes: "",
        },
      ],
    }));
    
    // Reset item form
    setSelectedItemId("");
    setItemQuantity(1);
  };

  const handleRemoveItem = (itemId) => {
    setFormData((prev) => ({
      ...prev,
      items: prev.items.filter((i) => i.item_id !== itemId),
    }));
  };

  const handleQuantityChange = (itemId, quantity) => {
    setFormData((prev) => ({
      ...prev,
      items: prev.items.map((i) =>
        i.item_id === itemId ? { ...i, quantity: parseInt(quantity) || 1 } : i
      ),
    }));
  };

  const handleTemplateToggle = (index, enabled) => {
    setFormData((prev) => ({
      ...prev,
      task_templates: prev.task_templates.map((t, i) =>
        i === index ? { ...t, is_enabled: enabled } : t
      ),
    }));
  };

  const handleTemplateDeadlineChange = (index, days) => {
    setFormData((prev) => ({
      ...prev,
      task_templates: prev.task_templates.map((t, i) =>
        i === index ? { ...t, deadline_days: parseInt(days) || t.deadline_days } : t
      ),
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.customer_id) {
      toast.error("Please select a customer");
      return;
    }

    if (!formData.target_delivery_date) {
      toast.error("Please select a target delivery date");
      return;
    }

    if (formData.items.length === 0) {
      toast.error("Please add at least one item");
      return;
    }

    setSubmitting(true);

    try {
      // Filter task_templates to only include expected fields
      const cleanedTemplates = formData.task_templates.map(t => ({
        task_name: t.task_name,
        department: t.department,
        deadline_days: t.deadline_days,
        is_enabled: t.is_enabled,
        notes: t.notes || null,
      }));

      const response = await axios.post(
        `${API}/orders`,
        {
          customer_id: formData.customer_id,
          target_delivery_date: formData.target_delivery_date.toISOString(),
          notes: formData.notes || null,
          po_number: formData.po_number || null,
          shipping_address: formData.shipping_address || null,
          billing_address: formData.billing_address || null,
          payment_terms: formData.payment_terms || null,
          items: formData.items.map(item => ({
            item_id: item.item_id,
            item_name: item.item_name,
            quantity: item.quantity,
            specifications: item.specifications || null,
            notes: item.notes || null,
          })),
          task_templates: cleanedTemplates,
          submit_for_approval: formData.submit_for_approval,
        },
        { withCredentials: true }
      );

      toast.success(
        formData.submit_for_approval 
          ? `Order ${response.data.order_number} submitted for approval`
          : `Order ${response.data.order_number} saved as draft`
      );
      navigate(`/orders/${response.data.order_id}`);
    } catch (err) {
      console.error("Failed to create order:", err);
      toast.error(err.response?.data?.detail || "Failed to create order");
    } finally {
      setSubmitting(false);
    }
  };

  // Get the set of category IDs from all items in the order
  const orderCategoryIds = new Set(
    formData.items
      .map((i) => i.category_id)
      .filter(Boolean)
  );

  // Auto-update template enablement when items change
  useEffect(() => {
    if (taskTemplates.length === 0) return;
    
    const itemCatIds = new Set(
      formData.items.map((i) => i.category_id).filter(Boolean)
    );

    // Update task_templates: only enable templates matching item categories
    const updatedTemplates = taskTemplates.map((t) => {
      const tmplCats = t.categories || [];
      // Template has no category assignments -> applies to ALL items (always enabled)
      if (tmplCats.length === 0) {
        return { ...t, is_enabled: true };
      }
      if (itemCatIds.size === 0) {
        // No items added yet -> enable all that have categories
        return { ...t, is_enabled: true };
      }
      // Enable only if template categories overlap with item categories
      const matches = tmplCats.some((catId) => itemCatIds.has(catId));
      return { ...t, is_enabled: matches };
    });

    setFormData((prev) => ({ ...prev, task_templates: updatedTemplates }));
  }, [formData.items, taskTemplates]);

  // Group templates by department - only show templates relevant to order items
  const templatesByDept = formData.task_templates.reduce((acc, template, index) => {
    const tmplCats = template.categories || [];
    
    // Templates with no category assignments apply to ALL items - always show them
    if (tmplCats.length === 0) {
      if (!acc[template.department]) {
        acc[template.department] = [];
      }
      acc[template.department].push({ ...template, index });
      return acc;
    }
    
    // If items are added, hide templates that don't match any item category
    if (orderCategoryIds.size > 0) {
      const matches = tmplCats.some((catId) => orderCategoryIds.has(catId));
      if (!matches) return acc;
    }

    if (!acc[template.department]) {
      acc[template.department] = [];
    }
    acc[template.department].push({ ...template, index });
    return acc;
  }, {});

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-6 h-6 animate-spin" />
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto space-y-6" data-testid="create-order-page">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => navigate(-1)}
          className="rounded-sm"
        >
          <ArrowLeft className="w-4 h-4" />
        </Button>
        <div>
          <h1 className="font-heading text-3xl tracking-tight">Create Order</h1>
          <p className="text-muted-foreground mt-1">
            Create a new sales order with customizable tasks
          </p>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Customer & Basic Info */}
        <Card className="border border-border shadow-none rounded-sm">
          <CardHeader>
            <CardTitle className="font-heading text-xl">Order Details</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="customer">Customer *</Label>
                <Select
                  value={formData.customer_id}
                  onValueChange={(value) =>
                    setFormData((prev) => ({ ...prev, customer_id: value }))
                  }
                >
                  <SelectTrigger className="rounded-sm" data-testid="customer-select">
                    <SelectValue placeholder="Select customer" />
                  </SelectTrigger>
                  <SelectContent>
                    {customers.map((customer) => (
                      <SelectItem key={customer.customer_id} value={customer.customer_id}>
                        {customer.name} ({customer.code})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Target Delivery Date *</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className="w-full justify-start text-left font-normal rounded-sm"
                      data-testid="delivery-date-button"
                    >
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {formData.target_delivery_date
                        ? format(formData.target_delivery_date, "PPP")
                        : "Pick a date"}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={formData.target_delivery_date}
                      onSelect={(date) =>
                        setFormData((prev) => ({ ...prev, target_delivery_date: date }))
                      }
                      disabled={(date) => date < new Date()}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="po_number">PO Number</Label>
                <Input
                  id="po_number"
                  placeholder="Customer PO number"
                  value={formData.po_number}
                  onChange={(e) =>
                    setFormData((prev) => ({ ...prev, po_number: e.target.value }))
                  }
                  className="rounded-sm"
                  data-testid="po-number-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="payment_terms">Payment Terms</Label>
                <Input
                  id="payment_terms"
                  placeholder="e.g., Net 30"
                  value={formData.payment_terms}
                  onChange={(e) =>
                    setFormData((prev) => ({ ...prev, payment_terms: e.target.value }))
                  }
                  className="rounded-sm"
                  data-testid="payment-terms-input"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="shipping_address">Shipping Address</Label>
                <Textarea
                  id="shipping_address"
                  placeholder="Full shipping address"
                  value={formData.shipping_address}
                  onChange={(e) =>
                    setFormData((prev) => ({ ...prev, shipping_address: e.target.value }))
                  }
                  className="rounded-sm"
                  rows={2}
                  data-testid="shipping-address-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="billing_address">Billing Address</Label>
                <Textarea
                  id="billing_address"
                  placeholder="Full billing address"
                  value={formData.billing_address}
                  onChange={(e) =>
                    setFormData((prev) => ({ ...prev, billing_address: e.target.value }))
                  }
                  className="rounded-sm"
                  rows={2}
                  data-testid="billing-address-input"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="notes">Notes</Label>
              <Textarea
                id="notes"
                placeholder="Any special instructions..."
                value={formData.notes}
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, notes: e.target.value }))
                }
                className="rounded-sm"
                data-testid="order-notes"
              />
            </div>
          </CardContent>
        </Card>

        {/* Order Items with Enhanced Specifications */}
        <Card className="border border-border shadow-none rounded-sm">
          <CardHeader>
            <CardTitle className="font-heading text-xl">Order Items</CardTitle>
            <CardDescription>Add items with specifications</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Add Item Form */}
            <div className="p-4 border border-dashed border-border rounded-sm space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label>Select Item</Label>
                  <Select value={selectedItemId} onValueChange={setSelectedItemId}>
                    <SelectTrigger className="rounded-sm" data-testid="item-select">
                      <SelectValue placeholder="Select item" />
                    </SelectTrigger>
                    <SelectContent>
                      {items.map((item) => (
                        <SelectItem key={item.item_id} value={item.item_id}>
                          {item.name} ({item.sku})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Quantity</Label>
                  <Input
                    type="number"
                    min="1"
                    value={itemQuantity}
                    onChange={(e) => setItemQuantity(parseInt(e.target.value) || 1)}
                    className="rounded-sm"
                    data-testid="item-quantity-input"
                  />
                </div>
                <div className="flex items-end">
                  <Button
                    type="button"
                    onClick={handleAddItem}
                    disabled={!selectedItemId}
                    className="rounded-sm w-full"
                    data-testid="add-item-button"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Add Item
                  </Button>
                </div>
              </div>
            </div>

            {/* Added Items List */}
            {formData.items.length > 0 ? (
              <div className="divide-y divide-border border border-border rounded-sm">
                {formData.items.map((item, index) => (
                  <div
                    key={item.item_id}
                    className="flex items-center gap-4 p-4"
                    data-testid={`order-item-${item.item_id}`}
                  >
                    <div className="w-10 h-10 rounded-sm bg-muted flex items-center justify-center">
                      <Package className="w-5 h-5 text-muted-foreground" />
                    </div>
                    <div className="flex-1">
                      <p className="font-medium">{item.item_name}</p>
                      {item.category_name && (
                        <Badge variant="outline" className="text-xs mt-1">
                          {item.category_name}
                        </Badge>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      <Label className="text-xs">Qty:</Label>
                      <Input
                        type="number"
                        min="1"
                        value={item.quantity}
                        onChange={(e) =>
                          handleQuantityChange(item.item_id, e.target.value)
                        }
                        className="w-20 rounded-sm"
                      />
                    </div>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      onClick={() => handleRemoveItem(item.item_id)}
                      className="text-muted-foreground hover:text-destructive"
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 border border-dashed border-border rounded-sm">
                <Package className="w-10 h-10 text-muted-foreground/30 mx-auto mb-2" />
                <p className="text-sm text-muted-foreground">No items added yet</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Task Templates (Collapsible) */}
        <Card className="border border-border shadow-none rounded-sm">
          <CardHeader
            className="cursor-pointer"
            onClick={() => setShowTaskTemplates(!showTaskTemplates)}
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Settings2 className="w-5 h-5 text-muted-foreground" />
                <CardTitle className="font-heading text-xl">Task Configuration</CardTitle>
              </div>
              <div className="flex items-center gap-2">
                <Badge variant="secondary" className="font-mono">
                  {formData.task_templates.filter(t => t.is_enabled).length} of {Object.values(templatesByDept).flat().length} tasks enabled
                </Badge>
                {showTaskTemplates ? (
                  <ChevronUp className="w-5 h-5 text-muted-foreground" />
                ) : (
                  <ChevronDown className="w-5 h-5 text-muted-foreground" />
                )}
              </div>
            </div>
            <CardDescription>
              {formData.items.length > 0
                ? "Showing tasks matching your item categories. Toggle or adjust deadlines."
                : "Add items first to see relevant task templates for their categories."}
            </CardDescription>
          </CardHeader>
          
          {showTaskTemplates && (
            <CardContent className="space-y-4">
              <Tabs defaultValue="merchandizing">
                <TabsList className="bg-muted/50 flex-wrap h-auto p-1">
                  {Object.keys(templatesByDept).map((dept) => (
                    <TabsTrigger key={dept} value={dept} className="rounded-sm capitalize">
                      {DEPARTMENT_LABELS[dept] || dept}
                    </TabsTrigger>
                  ))}
                </TabsList>
                
                {Object.entries(templatesByDept).map(([dept, templates]) => {
                  const colors = DEPARTMENT_COLORS[dept] || DEPARTMENT_COLORS.merchandizing;
                  return (
                    <TabsContent key={dept} value={dept} className="space-y-2 mt-4">
                      <div className="space-y-2">
                        {templates.map((template) => (
                          <div
                            key={template.index}
                            className={cn(
                              "flex items-center gap-4 p-3 border rounded-sm",
                              template.is_enabled ? "bg-background" : "bg-muted/30 opacity-60"
                            )}
                          >
                            <Switch
                              checked={template.is_enabled}
                              onCheckedChange={(checked) =>
                                handleTemplateToggle(template.index, checked)
                              }
                              data-testid={`template-toggle-${template.index}`}
                            />
                            <div className="flex-1">
                              <p className="text-sm font-medium">{template.task_name}</p>
                            </div>
                            <div className="flex items-center gap-2">
                              <Label className="text-xs text-muted-foreground">Days:</Label>
                              <Input
                                type="number"
                                min="1"
                                value={template.deadline_days}
                                onChange={(e) =>
                                  handleTemplateDeadlineChange(template.index, e.target.value)
                                }
                                className="w-16 h-8 rounded-sm text-center font-mono"
                                disabled={!template.is_enabled}
                                data-testid={`template-days-${template.index}`}
                              />
                            </div>
                          </div>
                        ))}
                      </div>
                    </TabsContent>
                  );
                })}
              </Tabs>
            </CardContent>
          )}
        </Card>

        {/* Submit Options */}
        <Card className="border border-border shadow-none rounded-sm">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Switch
                  checked={formData.submit_for_approval}
                  onCheckedChange={(checked) =>
                    setFormData((prev) => ({ ...prev, submit_for_approval: checked }))
                  }
                  data-testid="submit-for-approval-switch"
                />
                <div>
                  <p className="font-medium">Submit for Approval</p>
                  <p className="text-xs text-muted-foreground">
                    {formData.submit_for_approval
                      ? "Order will be sent to admin for approval"
                      : "Order will be saved as draft"}
                  </p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Submit Buttons */}
        <div className="flex justify-end gap-4">
          <Button
            type="button"
            variant="outline"
            onClick={() => navigate(-1)}
            className="rounded-sm"
          >
            Cancel
          </Button>
          <Button
            type="submit"
            disabled={submitting}
            className="rounded-sm min-w-[160px]"
            data-testid="submit-order-button"
          >
            {submitting ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Creating...
              </>
            ) : formData.submit_for_approval ? (
              "Submit for Approval"
            ) : (
              "Save as Draft"
            )}
          </Button>
        </div>
      </form>
    </div>
  );
}
